"use client";

import { useEffect } from "react";

export function DebugAttributeInjector() {
  useEffect(() => {
    // Only run in development or preview mode
    const isDevelopment = process.env.NODE_ENV === "development";
    const isPreview = process.env.NEXT_PUBLIC_PAGES_BRANCH === "preview";

    console.log("DebugAttributeInjector - Environment check:", {
      NODE_ENV: process.env.NODE_ENV,
      NEXT_PUBLIC_PAGES_BRANCH: process.env.NEXT_PUBLIC_PAGES_BRANCH,
      isDevelopment,
      isPreview,
      willRun: isDevelopment || isPreview,
    });

    if (!isDevelopment && !isPreview) {
      console.log(
        "DebugAttributeInjector - Skipping: not in development or preview mode"
      );
      return;
    }

    console.log("DebugAttributeInjector - Starting debug attribute injection");

    let componentCounter = 0;

    function addDebugAttributesToElement(element: Element) {
      // Skip if already has debug attributes
      if (element.hasAttribute("data-component-path")) {
        return;
      }

      // Only target common HTML tags used in React components
      const tagName = element.tagName.toLowerCase();
      const allowedTags = [
        "div",
        "section",
        "span",
        "form",
        "article",
        "aside",
        "header",
        "footer",
        "main",
        "nav",
        "p",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "button",
        "input",
        "textarea",
        "select",
        "label",
        "fieldset",
        "legend",
        "table",
        "thead",
        "tbody",
        "tr",
        "td",
        "th",
        "figure",
        "figcaption",
        "img",
        "video",
        "audio",
        "canvas",
        "svg",
      ];

      if (!allowedTags.includes(tagName)) {
        return;
      }

      // Skip elements that are clearly not components
      if (
        element.getAttribute("data-radix-popper-content-wrapper") ||
        element.getAttribute("data-radix-portal")
      ) {
        return;
      }

      try {
        // Try to get component info from React fiber
        const reactFiber =
          (element as any)._reactInternalFiber ||
          (element as any).__reactInternalInstance ||
          Object.keys(element).find(
            (key) =>
              key.startsWith("__reactFiber") ||
              key.startsWith("__reactInternalInstance")
          );

        let componentName = "UnknownComponent";
        let componentPath = "unknown";
        let componentFile = "unknown";

        // Try to extract component name from various sources
        if (reactFiber) {
          const fiber =
            typeof reactFiber === "string"
              ? (element as any)[reactFiber]
              : reactFiber;
          if (fiber && fiber.type) {
            if (typeof fiber.type === "function") {
              componentName =
                fiber.type.name ||
                fiber.type.displayName ||
                "AnonymousComponent";
            } else if (typeof fiber.type === "string") {
              componentName = fiber.type;
            }
          }
        }

        // Fallback: use class names or element characteristics to guess component name
        if (componentName === "UnknownComponent") {
          const className = element.className;
          if (typeof className === "string" && className) {
            // Try to extract component name from CSS classes
            const classNames = className.split(" ");
            const componentClass = classNames.find(
              (cls) =>
                cls.includes("Component") ||
                cls.match(/^[A-Z][a-zA-Z]*$/) ||
                cls.includes("-component")
            );
            if (componentClass) {
              componentName = componentClass;
            }
          }

          // Use tag name for HTML elements
          if (componentName === "UnknownComponent") {
            componentName = tagName;
          }
        }

        // Try to get file path from error stack (this is a heuristic approach)
        try {
          const error = new Error();
          const stack = error.stack;
          if (stack) {
            const stackLines = stack.split("\n");
            for (let i = 0; i < stackLines.length; i++) {
              const match =
                stackLines[i].match(/at.*\((.+):(\d+):(\d+)\)/) ||
                stackLines[i].match(/at (.+):(\d+):(\d+)/);

              if (
                match &&
                match[1] &&
                !match[1].includes("node_modules") &&
                !match[1].includes("DebugAttributeInjector") &&
                (match[1].includes("/components/") ||
                  match[1].includes("/app/"))
              ) {
                componentPath = match[1].replace(/\\/g, "/").replace(/^\//, "");
                componentFile = componentPath.split("/").pop() || "unknown";
                break;
              }
            }
          }
        } catch (e) {
          // Ignore errors in stack trace parsing
        }

        // Generate unique ID
        componentCounter++;
        const tabId = `${componentPath}:${componentName}:${componentCounter}`;

        // Add debug attributes
        element.setAttribute("data-tab-id", tabId);
        element.setAttribute("data-tab-name", componentName);
        element.setAttribute("data-component-path", componentPath);
        element.setAttribute("data-component-file", componentFile);
        element.setAttribute("data-component-name", componentName);
        element.setAttribute("data-component-line", "0"); // We can't reliably get line numbers at runtime
      } catch (error) {
        // Silently fail if we can't add debug attributes
        console.warn("Could not add debug attributes to element:", error);
      }
    }

    function processExistingElements() {
      // Add debug attributes to existing elements
      const allElements = document.querySelectorAll("*");
      allElements.forEach(addDebugAttributesToElement);
    }

    function setupMutationObserver() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Process added nodes
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              addDebugAttributesToElement(element);

              // Also process child elements
              const childElements = element.querySelectorAll("*");
              childElements.forEach(addDebugAttributesToElement);
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false,
      });

      return observer;
    }

    // Process existing elements first
    processExistingElements();

    // Set up observer for new elements
    const observer = setupMutationObserver();

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  return null; // This component doesn't render anything
}
